import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, whatsappLink } from "@/lib/api";
import { useQuote } from "@/context/QuoteContext";
import { useLang } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star, MapPin, ShieldCheck, Globe, MessageCircle, Phone, Check, ArrowLeft, Award, Smile, PlayCircle, Sparkles, Plane, Signpost, CalendarDays, ChevronLeft, ChevronRight, FlaskConical } from "lucide-react";
import { HERO_FILL } from "@/data/heroFill";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import StoryCard from "@/components/StoryCard";

const HIGHLIGHTS = {
  "Dental World Progreso": [
    { icon: Signpost, key: "First clinic after the bridge" },
    { icon: CalendarDays, key: "Open Sundays" },
  ],
};

const PROXIMITY = {
  "Mexicali": "border", "Tijuana": "border", "Los Algodones": "border", "Nuevo Progreso": "border",
  "Cancún": "air", "Bogotá": "air", "San José": "air", "Istanbul": "air",
};

function ClinicVideo({ v, i }) {
  const [landscape, setLandscape] = useState(false);
  const poster = v.url.replace(/\.mp4$/, "-poster.jpg");
  useEffect(() => {
    const img = new Image();
    img.onload = () => setLandscape(img.naturalWidth >= img.naturalHeight);
    img.src = poster;
  }, [poster]);
  return (
    <div className={`w-full rounded-2xl overflow-hidden border border-border bg-white shadow-sm ${landscape ? "sm:col-span-2 max-w-[720px]" : "max-w-[320px]"}`} data-testid={`clinic-video-${i}`}>
      <div className={`w-full bg-black ${landscape ? "aspect-video" : "aspect-[9/16]"}`}>
        <video
          src={v.url}
          poster={poster}
          controls
          playsInline
          preload="metadata"
          onLoadedMetadata={(e) => setLandscape(e.currentTarget.videoWidth >= e.currentTarget.videoHeight)}
          className="w-full h-full object-contain"
        />
      </div>
      {v.title && <div className="px-4 py-3 text-sm font-medium text-center">{v.title}</div>}
    </div>
  );
}

const PKG_OVERRIDES = {
  "Let's Smile Dentistry": { allOnName: "All-on 4/6 Full Arch", implants: "As many implants as necessary", nights: "Three nights of hotel per trip" },
  "Art Dental Care": { allOnName: "All-on 4/6 Full Arch", implants: "As many implants as necessary", nights: "Four nights of hotel per trip" },
  "Déntica by Cristina Suaza": { allOnName: "All-on 4/6 Full Arch", implants: "As many implants as necessary", nights: "Five nights of hotel per trip" },
  "Dentology Cancún": { allOnName: "All-on 4/6 Full Arch", implants: "As many implants as necessary", nights: "Five nights of hotel · Renaissance Hotel" },
  "Dental World Progreso": { allOnName: "All-on 4/6 Full Arch", implants: "As many implants as necessary", nights: "Three nights of hotel per trip" },
};

export default function ClinicDetail() {
  const { id } = useParams();
  const { openQuote } = useQuote();
  const { t, lang } = useLang();
  const [clinic, setClinic] = useState(null);
  const [stories, setStories] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => { api.get(`/clinics/${id}`).then((r) => { setClinic(r.data); setActive(0); }).catch(() => setClinic(false)); }, [id]);
  useEffect(() => {
    if (clinic && clinic.name) api.get("/testimonials", { params: { clinic: clinic.name } }).then((r) => setStories(r.data)).catch(() => setStories([]));
  }, [clinic]);

  if (clinic === false) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-muted-foreground">{t("clinic.notFound")} <Link to="/clinics" className="text-primary underline">{t("clinic.backToClinics")}</Link></div>;
  if (!clinic) return <div className="min-h-[50vh] flex items-center justify-center text-muted-foreground">{t("common.loading")}</div>;

  const to = (str) => t(`clinic.pkgo.${str}`);
  const buildPackages = (clinicName) => {
    const o = PKG_OVERRIDES[clinicName] || {};
    const allOn = {
      id: "all-on-4",
      name: o.allOnName ? to(o.allOnName) : t("clinic.packages.allOnDefault"),
      tagline: t("clinic.packages.allOnTagline"),
      items: [
        ...t("clinic.packages.allOnItems").slice(0, 5),
        o.implants ? to(o.implants) : t("clinic.packages.allOnItems")[5],
        ...t("clinic.packages.allOnItems").slice(6, 8),
        o.nights ? to(o.nights) : t("clinic.packages.allOnItems")[8],
        t("clinic.packages.allOnItems")[9],
      ],
    };
    const smile = {
      id: "smile-makeover",
      name: t("clinic.packages.smileName"),
      tagline: t("clinic.packages.smileTagline"),
      items: t("clinic.packages.smileItems"),
    };
    return [allOn, smile];
  };

  const gallery = [clinic.hero_image, ...(clinic.gallery || [])].filter(Boolean);
  const heroFill = active === 0 ? HERO_FILL[clinic.name] : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <Link to="/clinics" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-4" data-testid="back-to-clinics"><ArrowLeft className="w-4 h-4" /> {t("clinic.backToClinics")}</Link>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8 min-w-0">
          {/* Gallery */}
          <div>
            <div className={`relative rounded-2xl overflow-hidden aspect-[4/3] sm:aspect-[16/10] sm:max-h-[440px] border border-border flex items-center justify-center ${heroFill ? heroFill.bg : "bg-secondary/10"}`}>
              {(!heroFill || heroFill.blur) && <img src={gallery[active]} aria-hidden="true" alt="" className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-50" />}
              <img src={gallery[active]} alt={clinic.name} className="relative w-full h-full object-contain" data-testid="clinic-main-image" />
              {gallery.length > 1 && (
                <>
                  <button onClick={() => setActive((active - 1 + gallery.length) % gallery.length)} aria-label="Previous photo" data-testid="gallery-prev" className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white shadow-md flex items-center justify-center text-foreground transition-transform hover:scale-105">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button onClick={() => setActive((active + 1) % gallery.length)} aria-label="Next photo" data-testid="gallery-next" className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white shadow-md flex items-center justify-center text-foreground transition-transform hover:scale-105">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <span className="absolute bottom-3 right-3 text-xs font-medium bg-black/60 text-white px-2 py-0.5 rounded-full">{active + 1} / {gallery.length}</span>
                </>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {gallery.map((g, i) => (
                  <button key={i} onClick={() => setActive(i)} className={`shrink-0 rounded-lg overflow-hidden w-20 h-16 border-2 transition-colors bg-secondary/10 ${active === i ? "border-primary" : "border-transparent"}`} data-testid={`gallery-thumb-${i}`}>
                    <img src={g} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Header */}
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-heading text-3xl sm:text-4xl font-extrabold">{clinic.name}</h1>
              {clinic.featured && <Badge className="bg-accent hover:bg-accent">{t("clinic.featured")}</Badge>}
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {clinic.city}, {clinic.country}</span>
              <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-amber-400 text-amber-400" /> {clinic.rating} ({t("clinic.reviews", { n: clinic.reviews_count })})</span>
              <span className="flex items-center gap-1"><Award className="w-4 h-4" /> {t("clinic.yrsExp", { n: clinic.years_experience })}</span>
            </div>
            {HIGHLIGHTS[clinic.name] && (
              <div className="flex flex-wrap gap-2 mt-4" data-testid="clinic-highlights">
                {HIGHLIGHTS[clinic.name].map((h) => (
                  <span key={h.key} className="inline-flex items-center gap-1.5 rounded-full bg-accent/10 text-accent px-3.5 py-1.5 text-sm font-semibold">
                    <h.icon className="w-4 h-4 shrink-0" /> {t(`clinic.highlights.${h.key}`)}
                  </span>
                ))}
              </div>
            )}
            <p className="text-foreground/80 leading-relaxed mt-5">{lang === "es" && clinic.description_es ? clinic.description_es : clinic.description}</p>
          </div>

          {/* Certifications + languages */}
          <div className="grid sm:grid-cols-2 gap-5">
            <Card className="p-5 rounded-2xl">
              <h3 className="font-heading font-bold flex items-center gap-2 mb-3"><ShieldCheck className="w-5 h-5 text-primary" /> {t("clinic.certifications")}</h3>
              <div className="flex flex-wrap gap-2">{clinic.certifications?.map((c) => <span key={c} className="text-sm bg-secondary/5 text-secondary rounded-full px-3 py-1">{c}</span>)}</div>
            </Card>
            <Card className="p-5 rounded-2xl">
              <h3 className="font-heading font-bold flex items-center gap-2 mb-3"><Globe className="w-5 h-5 text-primary" /> {t("clinic.languagesSpoken")}</h3>
              <div className="flex flex-wrap gap-2">{clinic.languages?.map((l) => <span key={l} className="text-sm bg-primary/5 text-primary rounded-full px-3 py-1">{l}</span>)}</div>
            </Card>
          </div>

          {/* In-house lab highlight */}
          {clinic.lab && (
            <Card className="rounded-2xl overflow-hidden border-primary/20 shadow-sm" data-testid="clinic-lab">
              <div className="grid md:grid-cols-2">
                <div className="bg-black flex items-center justify-center">
                  <video src={clinic.lab.video} controls playsInline preload="metadata" className="w-full max-h-[440px] object-contain" data-testid="clinic-lab-video" />
                </div>
                <div className="p-6 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-primary font-semibold"><FlaskConical className="w-4 h-4" /> {t("clinic.inHouseLab")}</div>
                  <h3 className="font-heading text-2xl font-bold mt-2 leading-tight">{clinic.lab.title}</h3>
                  <ul className="mt-4 space-y-2.5">
                    {clinic.lab.points?.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm text-foreground/85"><Check className="w-4 h-4 text-accent shrink-0 mt-0.5" /> {p}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>
          )}

          {/* Packages */}
          {clinic.packages?.length > 0 && (
            <Card className="p-5 rounded-2xl">
              <h3 className="font-heading font-bold mb-3">{t("clinic.whatsIncluded")}</h3>
              <div className="grid sm:grid-cols-2 gap-2">
                {clinic.packages.map((p) => <div key={p} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-accent shrink-0" /> {p}</div>)}
              </div>
            </Card>
          )}

          {/* All-inclusive packages */}
          <div>
            <h3 className="font-heading text-2xl font-bold mb-1">{t("clinic.allInclusive")}</h3>
            <p className="text-sm text-muted-foreground mb-4">{t("clinic.allInclusiveSub")}</p>
            <div className="grid sm:grid-cols-2 gap-5">
              {buildPackages(clinic.name).map((pkg) => (
                <Card key={pkg.id} className="rounded-2xl overflow-hidden flex flex-col" data-testid={`package-${pkg.id}`}>
                  <div className="bg-primary text-white px-5 py-4">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-white/70"><Sparkles className="w-4 h-4" /> {t("clinic.allInclusiveBadge")}</div>
                    <h4 className="font-heading text-xl font-bold mt-1">{pkg.name}</h4>
                    <p className="text-sm text-white/85">{pkg.tagline}</p>
                  </div>
                  <div className="p-5 flex-1">
                    <ul className="space-y-2">
                      {pkg.items.map((it) => (
                        <li key={it} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-accent shrink-0 mt-0.5" /> {it}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="px-5 pb-5">
                    <p className="text-xs text-muted-foreground mb-2">{t("clinic.pricingFor")}</p>
                    <Button className="w-full rounded-full h-11" onClick={() => openQuote({ type: "quote", procedure: pkg.name, clinic: clinic.name })} data-testid={`package-cta-${pkg.id}`}>
                      {t("clinic.talkToCoordinator")}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Before & After */}
          {clinic.before_after?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Smile className="w-6 h-6 text-primary" />
                <h3 className="font-heading text-2xl font-bold">{t("clinic.smileTransforms")}</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">{t("clinic.dragSlider")}</p>
              <div className="grid sm:grid-cols-2 gap-5 items-start">
                {clinic.before_after.map((ba, i) => (
                  ba.steps
                    ? (
                      <div key={i} className="sm:col-span-2 rounded-2xl overflow-hidden border-2 border-primary/30 bg-white shadow-md" data-testid={`transformation-sequence-${i}`}>
                        <div className="px-5 pt-4 flex items-center gap-2">
                          <Award className="w-5 h-5 text-primary" />
                          <span className="text-xs font-semibold uppercase tracking-wider text-primary">{t("clinic.featuredCase")}</span>
                        </div>
                        <div className="px-5 pb-1 pt-1">
                          <h4 className="font-heading text-xl font-bold">{ba.label}</h4>
                          {ba.caption && <p className="text-sm text-muted-foreground mt-1">{ba.caption}</p>}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-5 pt-3">
                          {ba.steps.map((s, j) => (
                            <div key={j} className="relative">
                              <img src={s.image} alt={s.label} className="w-full aspect-[4/3] object-contain bg-black rounded-xl" />
                              <span className="absolute top-2 left-2 text-[11px] font-semibold bg-primary text-white px-2 py-1 rounded-full shadow">{j + 1}. {s.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                    : ba.before && ba.after
                    ? <BeforeAfterSlider key={i} before={ba.before} after={ba.after} label={ba.label} />
                    : (
                      <div key={i} className={`rounded-2xl overflow-hidden border border-border bg-white shadow-sm ${ba.wide ? "sm:col-span-2" : ""}`} data-testid={`before-after-${i}`}>
                        <img src={ba.image} alt={ba.label} className="w-full h-auto object-contain bg-black" />
                        <div className="px-4 py-3 text-sm font-medium">{ba.label}</div>
                      </div>
                    )
                ))}
              </div>
            </div>
          )}

          {/* Patient stories */}
          {stories.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-6 h-6 fill-amber-400 text-amber-400" />
                <h3 className="font-heading text-2xl font-bold">{t("clinic.patientStories")}</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">{t("clinic.patientStoriesSub")}</p>
              <div className="grid sm:grid-cols-2 gap-5">
                {stories.map((s, i) => <StoryCard key={s.id} story={s} index={i} />)}
              </div>
            </div>
          )}

          {/* Patient videos */}
          {clinic.videos?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <PlayCircle className="w-6 h-6 text-primary" />
                <h3 className="font-heading text-2xl font-bold">{t("clinic.patientVideos")}</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-6 justify-items-center">
                {clinic.videos.map((v, i) => (
                  <ClinicVideo key={i} v={v} i={i} />
                ))}
              </div>
            </div>
          )}

          {/* Map */}
          <div data-testid="location-section">
            <h3 className="font-heading text-2xl font-bold mb-4">{t("clinic.location")}</h3>
            <p className="text-sm text-muted-foreground mb-3 flex items-center gap-1"><MapPin className="w-4 h-4" /> {clinic.address}</p>
            {PROXIMITY[clinic.city] && (
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3.5 py-1.5 text-sm font-semibold" data-testid="border-note">
                {PROXIMITY[clinic.city] === "air"
                  ? <Plane className="w-4 h-4 shrink-0" />
                  : <MapPin className="w-4 h-4 shrink-0" />}
                {t(`clinic.proximity.${clinic.city}`)}
              </div>
            )}
            <div className="relative rounded-2xl overflow-hidden border border-border aspect-[16/7]">
              <iframe title="map" src={`https://www.google.com/maps?q=${encodeURIComponent(clinic.name + ", " + clinic.address)}&z=14&output=embed`} className="w-full h-full pointer-events-none" loading="lazy" data-testid="clinic-map" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">{t("clinic.mapNote")}</p>
          </div>
        </div>

        {/* Sticky CTA */}
        <div className="lg:sticky lg:top-24 h-fit min-w-0">
          <Card className="p-6 rounded-2xl shadow-sm">
            <p className="text-xs uppercase tracking-wider text-primary font-semibold">{t("clinic.stickyEyebrow")}</p>
            <p className="font-heading font-extrabold text-2xl mt-1">{t("clinic.stickySave")}</p>
            <p className="text-sm text-muted-foreground mt-1">{t("clinic.stickySub")}</p>
            <div className="space-y-3 mt-5">
              <Button className="w-full rounded-full h-12" onClick={() => openQuote({ type: "quote", clinic_id: clinic.id, clinic_name: clinic.name })} data-testid="clinic-get-quote-btn">{t("common.getFreeQuote")}</Button>
              <Button variant="outline" className="w-full rounded-full h-12 border-secondary text-secondary hover:bg-secondary hover:text-white" onClick={() => openQuote({ type: "call", clinic_id: clinic.id, clinic_name: clinic.name })} data-testid="clinic-book-call-btn"><Phone className="w-4 h-4 mr-2" /> {t("common.bookCall")}</Button>
              <a href={whatsappLink(t("clinic.waMessage", { name: clinic.name }))} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full rounded-full h-12 border-accent text-accent hover:bg-accent hover:text-white" data-testid="clinic-whatsapp-btn"><MessageCircle className="w-4 h-4 mr-2" /> WhatsApp</Button>
              </a>
            </div>
            <div className="flex items-center justify-center gap-2 mt-4 text-sm font-medium text-accent" data-testid="response-time-badge">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-60"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent"></span>
              </span>
              {t("clinic.respond2h")}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
